import { useEffect, useMemo, useRef, useState } from 'react';
import { QueryKey } from '@tanstack/react-query';

import { getDisableCache } from '@store/globalVars/slice';
import { useAppSelector } from '@store/hooks';

/**
 * Custom hook that generates a cache key for React Query that can be used to force refetches
 * when cache is disabled globally.
 *
 * @template TQueryKey - The type of query key to return, extends QueryKey
 * @param refetchOnDisableCache - Whether to include cache-busting parameters in the key when cache is disabled. Defaults to true.
 * @returns A query key array that includes cache-busting parameters when cache is disabled
 *
 * @example
 * ```typescript
 * const queryKey = useQueryCacheKey();
 * const { data } = useQuery({
 *   queryKey: ['collections', ...queryKey],
 *   queryFn: fetchCollections
 * });
 * ```
 */
const useQueryCacheKey = <TQueryKey extends QueryKey = QueryKey>(
  refetchOnDisableCache: boolean = true
): TQueryKey => {
  const disableCache = useAppSelector(getDisableCache);

  /**
   * Counter that increments each time cache is disabled to force query refetch.
   * Initialized to 0 if cache is already disabled, -1 otherwise.
   */
  const [cacheBustCounter, setCacheBustCounter] = useState(() => (disableCache ? 0 : -1));

  /**
   * Track if the hook has been initialized to prevent incrementing on mount.
   */
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }

    if (disableCache) {
      setCacheBustCounter(prevCount => prevCount + 1);
    }
  }, [disableCache]);

  return useMemo(() => {
    if (!refetchOnDisableCache || (!disableCache && cacheBustCounter < 0)) {
      return [] as unknown as TQueryKey;
    }

    return ['no-cache', cacheBustCounter] as unknown as TQueryKey;
  }, [disableCache, cacheBustCounter, refetchOnDisableCache]);
};

export default useQueryCacheKey;
